import { ImageWithLoading } from '../../../../../shared/components/image-with-loading/image-with-loading';
import React from 'react';
import { BaseImageSettings, useImageSettings } from '../../../../../shared/hooks/useImageSettings';
import { useIsMobile } from '../../../../../shared/hooks/useIsMobile';
import WishlistButton from './wishlist-button';
import { ProductLabels } from '../../../../../shared/components/product-labels/product-labels';
import { CusProductListProductListItemProps } from '../../../cus-product-list.data';
import { PRODUCT_CARD_IMAGE_QUALITY, PRODUCT_LIST_MODULE_NAME } from '../../../cus-product-list.const';
import { ColorPalettePreview } from '../../../../../shared/components/color-palette-preview/color-palette-preview';
import { HtmlHeadInclude, ImageFetchPriority } from '@msdyn365-commerce/core';
import { MemberPriceTag } from '../../../../../shared/components/member-price-tag/member-price-tag';
import { useLogProductListItemError } from '../../../hooks/useLogProductListItemError';

const IMAGE_VIEWPORT_SETTINGS: BaseImageSettings = {
    xs: 'large',
    sm: 'large',
    md: 'medium',
    lg: 'medium',
    xl: 'large'
};

export default function ProductListItem({
    src,
    price,
    name,
    colorVariants,
    labels,
    gridSettings,
    relativeUrl,
    isInWishlist,
    onWishlistClick,
    listPrice,
    memberPrice,
    addToWishlistAriaLabel,
    removeFromWishlistAriaLabel,
    priority = false,
    context,
    isWishlistReady,
    variantId
}: CusProductListProductListItemProps) {
    const imageSettings = useImageSettings(IMAGE_VIEWPORT_SETTINGS);
    const isMobile = useIsMobile(context);
    const preloadQuery = isMobile ? imageSettings.xs?.q : imageSettings.lg?.q;
    const preloadImageUrl = preloadQuery ? `${src}?${preloadQuery}` : src;

    useLogProductListItemError({ variantId, name, price, src, relativeUrl, telemetry: context.telemetry });

    const handleWishlistClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onWishlistClick();
    };

    if (!variantId || !name || !price) {
        return null;
    }

    return (
        <div className='bs-product-list-container__item' data-testid='plp-product-card'>
            {priority && (
                <HtmlHeadInclude>
                    <link rel='preload' as='image' href={preloadImageUrl} />
                </HtmlHeadInclude>
            )}
            <a className='bs-product-list-container__item__link' href={relativeUrl} data-testid='plp-product-link'>
                <div className='bs-product-list-container__item__image-container'>
                    <ImageWithLoading
                        wrapperClassname='bs-product-list-container__item__image-wrapper'
                        src={src}
                        alt={name}
                        title={name}
                        imageSettings={{ viewports: imageSettings, quality: PRODUCT_CARD_IMAGE_QUALITY }}
                        gridSettings={gridSettings}
                        loadFailureBehavior='empty'
                        showEmptyImageThumbnail
                        location={PRODUCT_LIST_MODULE_NAME}
                        telemetry={context.telemetry}
                        variantConcatenatedId={variantId}
                        {...(priority ? { loading: 'eager' as const, fetchPriority: ImageFetchPriority.High } : {})}
                    />
                    <ProductLabels maxProductLabels={context.request.app?.config.maxProductLabels} labels={labels} />
                </div>
                <div className='bs-product-list-container__item__price-row'>
                    {memberPrice ? (
                        <>
                            <MemberPriceTag memberPrice={memberPrice} className='bs-product-list-container__item__price' />
                            <span className={'bs-product-list-container__item__price'}>{listPrice}</span>
                        </>
                    ) : (
                        <>
                            <span className={`bs-product-list-container__item__price${listPrice ? ' --sale' : ''}`}>{price}</span>
                            {listPrice && <span className='bs-product-list-container__item__price--original'>{listPrice}</span>}
                        </>
                    )}
                </div>
                <span className='bs-product-list-container__item__title' data-testid='plp-product-title'>
                    {name}
                </span>
                {colorVariants && <ColorPalettePreview colors={colorVariants} />}
            </a>
            <WishlistButton
                isInWishlist={isInWishlist}
                onClick={handleWishlistClick}
                addToWishlistAriaLabel={addToWishlistAriaLabel}
                removeFromWishlistAriaLabel={removeFromWishlistAriaLabel}
                isWishlistReady={isWishlistReady}
            />
        </div>
    );
}
